// PR-AK — Shared bulk-cancel confirm dialog for AdminScheduleGrid + AdminBookings.
//
// Two-phase UI:
//  1. SUMMARY phase: shows pre-cancel breakdown (paid/unpaid/free counts, refund
//     totals, waitlist count) + checkboxes. User confirms or cancels.
//  2. RESULTS phase (only if any failure): shows per-failure detail-list with
//     retry button (Q4 Option A). Successes are summarised.

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, Loader2, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import { useAdminBulkCancel, type BulkCancelOpts } from "@/hooks/useAdminBulkCancel";
import type { CancelBookingResult } from "@/lib/cancelBookingHelper";

export interface BulkCancelConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingIds: string[];
  /** Called after dialog closes successfully so parent can clear selection. */
  onCompleted?: (results: CancelBookingResult[]) => void;
}

type BookingPreview = {
  id: string;
  user_id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
  standard_price_total: number | null;
  free_reason: string | null;
  courts: { name: string | null } | null;
  profiles: { full_name: string | null } | null;
};

type PaymentRow = {
  id: string;
  booking_id: string;
  status: string;
  amount: number;
  stripe_payment_intent_id: string | null;
};

type WaitlistRow = {
  court_id: string;
  date: string;
  start_time: string;
};

export default function BulkCancelConfirmDialog({
  open,
  onOpenChange,
  bookingIds,
  onCompleted,
}: BulkCancelConfirmDialogProps) {
  const [refund, setRefund] = useState(true);
  const [triggerWaitlist, setTriggerWaitlist] = useState(true);
  const [chargeLateCancelFee, setChargeLateCancelFee] = useState(false);
  const [reason, setReason] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [results, setResults] = useState<CancelBookingResult[] | null>(null);

  const bulkCancel = useAdminBulkCancel();

  // Fetch booking + payment + waitlist info for the summary view.
  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ["bulk-cancel-preview-bookings", bookingIds.sort().join(",")],
    queryFn: async (): Promise<BookingPreview[]> => {
      if (bookingIds.length === 0) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select("id, user_id, court_id, start_time, end_time, status, standard_price_total, free_reason, courts(name), profiles:bookings_user_id_profiles_fkey(full_name)")
        .in("id", bookingIds);
      if (error) throw error;
      return (data ?? []) as any;
    },
    enabled: open && bookingIds.length > 0,
  });

  const { data: payments = [], isLoading: paymentsLoading } = useQuery({
    queryKey: ["bulk-cancel-preview-payments", bookingIds.sort().join(",")],
    queryFn: async (): Promise<PaymentRow[]> => {
      if (bookingIds.length === 0) return [];
      const { data, error } = await supabase
        .from("booking_payments")
        .select("id, booking_id, status, amount, stripe_payment_intent_id")
        .in("booking_id", bookingIds);
      if (error) throw error;
      return (data ?? []) as any;
    },
    enabled: open && bookingIds.length > 0,
  });

  // Fetch waitlist count for the affected (court, date, start_time) tuples.
  const { data: waitlistRows = [] } = useQuery({
    queryKey: ["bulk-cancel-preview-waitlist", bookings.map((b) => b.id).sort().join(",")],
    queryFn: async (): Promise<WaitlistRow[]> => {
      if (bookings.length === 0) return [];
      // Build OR filter — one (court_id, date, start_time) per booking.
      // Supabase JS doesn't support tuple-IN, so we issue one chained query
      // per unique slot. Most bulk-cancels touch <50 unique slots.
      const slots = new Set<string>();
      for (const b of bookings) {
        const date = format(new Date(b.start_time), "yyyy-MM-dd");
        const time = format(new Date(b.start_time), "HH:mm:ss");
        slots.add(`${b.court_id}|${date}|${time}`);
      }
      const all: WaitlistRow[] = [];
      for (const slot of slots) {
        const [court_id, date, start_time] = slot.split("|");
        const { data } = await supabase
          .from("waitlist")
          .select("court_id, date, start_time")
          .eq("court_id", court_id)
          .eq("date", date)
          .eq("start_time", start_time)
          .eq("status", "waiting");
        if (data) all.push(...(data as any));
      }
      return all;
    },
    enabled: open && bookings.length > 0,
  });

  // Reset state on open / close.
  useEffect(() => {
    if (!open) {
      setReason("");
      setShowAll(false);
      setResults(null);
      bulkCancel.reset();
    }
  }, [open]);

  // Default waitlist checkbox: ON if any waitlist row exists.
  useEffect(() => {
    if (open && waitlistRows.length > 0) {
      setTriggerWaitlist(true);
    }
  }, [open, waitlistRows.length]);

  // Compute summary from fetched data.
  const summary = useMemo(() => {
    const paymentsByBooking = new Map<string, PaymentRow[]>();
    for (const p of payments) {
      const arr = paymentsByBooking.get(p.booking_id) ?? [];
      arr.push(p);
      paymentsByBooking.set(p.booking_id, arr);
    }

    let paidBookings = 0;
    let unpaidBookings = 0;
    let freeBookings = 0;
    let stripePayments = 0;
    let manualPayments = 0;
    let totalPaidAmount = 0;

    for (const b of bookings) {
      const ps = paymentsByBooking.get(b.id) ?? [];
      const paidPs = ps.filter((p) => ["paid", "manual"].includes(p.status));
      if (b.free_reason) {
        freeBookings++;
      } else if (paidPs.length === 0) {
        unpaidBookings++;
      } else {
        paidBookings++;
        for (const p of paidPs) {
          totalPaidAmount += p.amount;
          if (p.stripe_payment_intent_id) stripePayments++;
          else manualPayments++;
        }
      }
    }

    return {
      total: bookings.length,
      paidBookings,
      unpaidBookings,
      freeBookings,
      stripePayments,
      manualPayments,
      totalPaidAmount,
      waitlistCount: waitlistRows.length,
    };
  }, [bookings, payments, waitlistRows]);

  const handleConfirm = async () => {
    const opts: BulkCancelOpts = {
      bookingIds,
      refund,
      reason,
      triggerWaitlistOffer: triggerWaitlist,
      chargeLateCancelFee,
    };
    try {
      const out = await bulkCancel.mutateAsync(opts);
      setResults(out.results);
      // Soft warning if email-dispatch failed (bookings still cancelled).
      if (out.emailDispatchError) {
        toast.warning(`Avbokningar gjorda men e-post misslyckades: ${out.emailDispatchError}`);
      }
      // If all succeeded, auto-close + notify parent. If any failed, stay open
      // on results phase so admin can review + retry.
      const anyFailed = out.results.some((r) => !r.success);
      if (!anyFailed) {
        onCompleted?.(out.results);
        onOpenChange(false);
      }
    } catch (err) {
      // Hotfix post-PR-AK: surface mutation errors via toast so admin gets
      // immediate feedback instead of a silent button-does-nothing.
      console.error("[BulkCancelConfirmDialog] mutation failed:", err);
      toast.error(
        err instanceof Error
          ? `Bulk-avbokning misslyckades: ${err.message}`
          : "Bulk-avbokning misslyckades — okänt fel",
      );
    }
  };

  const handleRetryFailed = async () => {
    if (!results) return;
    const failedIds = results.filter((r) => !r.success).map((r) => r.bookingId);
    if (failedIds.length === 0) return;

    try {
      const out = await bulkCancel.mutateAsync({
        bookingIds: failedIds,
        refund,
        reason,
        triggerWaitlistOffer: triggerWaitlist,
        chargeLateCancelFee,
      });
      if (out.emailDispatchError) {
        toast.warning(`Avbokningar gjorda men e-post misslyckades: ${out.emailDispatchError}`);
      }
      // Merge new results with previous successes.
      const previousSuccesses = results.filter((r) => r.success);
      const merged = [...previousSuccesses, ...out.results];
      setResults(merged);
      const stillFailed = merged.some((r) => !r.success);
      if (!stillFailed) {
        onCompleted?.(merged);
        onOpenChange(false);
      }
    } catch (err) {
      console.error("[BulkCancelConfirmDialog] retry failed:", err);
      toast.error(
        err instanceof Error
          ? `Försök igen misslyckades: ${err.message}`
          : "Försök igen misslyckades — okänt fel",
      );
    }
  };

  const handleCloseAfterPartial = () => {
    if (results) onCompleted?.(results);
    onOpenChange(false);
  };

  const isLoading = bookingsLoading || paymentsLoading;
  const isPending = bulkCancel.isPending;

  // Phase: results (after at least one mutation has run with failures).
  if (results) {
    const successes = results.filter((r) => r.success);
    const failures = results.filter((r) => !r.success);
    const refundFailedCount = successes.filter((r) => r.refundFailed).length;
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Avbokning klar</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 overflow-y-auto flex-1">
            <div className="flex gap-2 flex-wrap">
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {successes.length} lyckades
              </Badge>
              {failures.length > 0 && (
                <Badge variant="destructive">
                  <XCircle className="h-3 w-3 mr-1" />
                  {failures.length} misslyckades
                </Badge>
              )}
              {refundFailedCount > 0 && (
                <Badge variant="outline" className="text-yellow-800 border-yellow-300">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {refundFailedCount} refund-fel
                </Badge>
              )}
            </div>

            {failures.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Misslyckade bokningar:</p>
                <ScrollArea className="max-h-64 rounded-md border">
                  <ul className="divide-y">
                    {failures.map((f) => {
                      const b = bookings.find((bk) => bk.id === f.bookingId);
                      return (
                        <li key={f.bookingId} className="p-3 text-sm">
                          <p className="font-medium">
                            {b?.courts?.name ?? "Bana"} ·{" "}
                            {b ? format(new Date(b.start_time), "EEE d MMM HH:mm", { locale: sv }) : "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {b?.profiles?.full_name ?? "—"}
                          </p>
                          <p className="text-xs text-destructive mt-1">{f.error ?? "Okänt fel"}</p>
                        </li>
                      );
                    })}
                  </ul>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleCloseAfterPartial} disabled={isPending}>
              Stäng
            </Button>
            {failures.length > 0 && (
              <Button onClick={handleRetryFailed} disabled={isPending}>
                {isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Försöker igen...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Försök igen ({failures.length})</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Phase: summary.
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Avboka {bookingIds.length} bokningar</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">Hämtar information...</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Dessa bokningar avbokas. Spelare som redan betalat får återbetalning enligt nedan.
              </p>

              {/* Summary box */}
              <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span>Totalt markerade:</span>
                  <span className="font-medium">{summary.total}</span>
                </div>
                <div className="flex justify-between">
                  <span>Betalda:</span>
                  <span className="font-medium">{summary.paidBookings}</span>
                </div>
                <div className="flex justify-between">
                  <span>Obetalda:</span>
                  <span className="font-medium">{summary.unpaidBookings}</span>
                </div>
                <div className="flex justify-between">
                  <span>Gratis:</span>
                  <span className="font-medium">{summary.freeBookings}</span>
                </div>
                {summary.totalPaidAmount > 0 && (
                  <>
                    <div className="border-t pt-1.5 mt-1.5" />
                    <div className="flex justify-between">
                      <span>Total betalt:</span>
                      <span className="font-semibold">{summary.totalPaidAmount} kr</span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Stripe-betalningar:</span>
                      <span>{summary.stripePayments}</span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Manuella betalningar:</span>
                      <span>{summary.manualPayments}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Refund checkbox — only if any paid */}
              {summary.totalPaidAmount > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3">
                  <Checkbox
                    id="bulk-refund"
                    checked={refund}
                    onCheckedChange={(v) => setRefund(v === true)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="bulk-refund" className="text-sm text-yellow-900 cursor-pointer leading-snug">
                    Återbetala alla betalningar ({summary.totalPaidAmount} kr / {summary.paidBookings} bokningar)
                  </Label>
                </div>
              )}

              {/* Waitlist checkbox — defaults checked if any waitlist row exists */}
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="bulk-waitlist"
                  checked={triggerWaitlist}
                  onCheckedChange={(v) => setTriggerWaitlist(v === true)}
                  className="mt-0.5"
                  disabled={summary.waitlistCount === 0}
                />
                <Label
                  htmlFor="bulk-waitlist"
                  className={`text-sm cursor-pointer leading-snug ${summary.waitlistCount === 0 ? "text-muted-foreground" : ""}`}
                >
                  Erbjud lediga tider till väntelista
                  {summary.waitlistCount > 0 ? (
                    <span className="text-xs text-muted-foreground block">
                      {summary.waitlistCount} {summary.waitlistCount === 1 ? "person" : "personer"} på väntelista
                    </span>
                  ) : (
                    <span className="text-xs block">Ingen på väntelista för dessa tider</span>
                  )}
                </Label>
              </div>

              {/* Late-cancel-fee checkbox — admin opt-in (default UNCHECKED) */}
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="bulk-late-fee"
                  checked={chargeLateCancelFee}
                  onCheckedChange={(v) => setChargeLateCancelFee(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="bulk-late-fee" className="text-sm cursor-pointer leading-snug">
                  Ta ut late-cancel-fee enligt klubbens regler
                  <span className="text-xs text-muted-foreground block">
                    Skapar en pending-betalning per bokning (admin-opt-in)
                  </span>
                </Label>
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <Label htmlFor="bulk-reason" className="text-xs">Avbokningsorsak (valfritt)</Label>
                <Input
                  id="bulk-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="T.ex. teknisk störning"
                  className="h-8 text-sm"
                />
              </div>

              {/* Expandable booking list */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs text-primary hover:underline"
                >
                  {showAll ? "Dölj" : "Visa"} alla {bookings.length} bokningar
                </button>
                {showAll && (
                  <ScrollArea className="mt-2 max-h-48 rounded-md border">
                    <ul className="divide-y text-xs">
                      {bookings.map((b) => (
                        <li key={b.id} className="p-2">
                          <span className="font-medium">{b.courts?.name ?? "Bana"}</span>{" "}
                          ·{" "}
                          {format(new Date(b.start_time), "EEE d MMM HH:mm", { locale: sv })}
                          ·{" "}
                          <span className="text-muted-foreground">
                            {b.profiles?.full_name ?? "—"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Avbryt
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending || isLoading || bookingIds.length === 0}
          >
            {isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Avbokar...</>
            ) : (
              `Ja, avboka alla (${bookingIds.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
